import type { View } from "@novorender/api";
import type {
    BoundingSphere,
    HierarcicalObjectReference,
    ObjectData,
    ObjectId,
    SearchPattern,
} from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";
import pMap from "p-map";

import { type AppObjectDB, ObjectAccessForbiddenError } from "utils/appObjectDb";
import { mergeBoundingSpheres, unionAABB3 } from "utils/math";
import { getNodeClass, getParentPath, getParentPaths, isLeafNode, NodeClass } from "utils/objectData";
import { batchedPropertySearch, getDescendants, preloadDescendantsFastPath } from "utils/search";

import {
    highlightIdsFor,
    resolveSelectionLegacy,
    selectionTargetFor,
    singleSelectionTarget,
} from "./selectionResolution";
import {
    type BoundsOptions,
    type FindIdsOptions,
    type FindOptions,
    type ObjectDataAccess,
    type PropertyQuery,
    type PropertyTerm,
    type SelectionBounds,
    type SelectionResolutionMode,
    type SelectionResolutionOptions,
    type SelectionTarget,
} from "./types";
import { getObjectsByIdsOffline, objectBoundsAsSelection, oneObjectAsListing, singleIdPropertyQuery } from "./utils";

/**
 * Cap on raw records an ancestor walk will read — a safety net against a
 * pathological stream, not a bound on the chain itself (which is known from the
 * path). Aligned with the model tree's own scan budget.
 */
const MAX_ANCESTOR_SCAN = 5000;

/**
 * How many ids one `/resolve` request carries.
 *
 * Ours, not the endpoint's: it places no ceiling on the list, so the client picks a width
 * that keeps the request body well inside any limit an intermediary is likely to impose.
 * Nothing breaks at a smaller value except request count.
 */
const RESOLVE_BATCH_SIZE = 50_000;

/** How many `/resolve` batches are in flight at once — the search fan-out's order. */
const RESOLVE_CONCURRENCY = 5;

/**
 * A query term as a `SearchPattern`. `exact` is what makes an equality match rather
 * than the server's default substring one — so `contains` is the same pattern without
 * it. `range` and a bare `property` (the "present at all" probe) carry no value of
 * their own.
 */
function toSearchPattern(term: PropertyTerm): SearchPattern {
    const pattern: SearchPattern = { property: term.property, exclude: term.negate };
    if (term.anyOf) {
        return { ...pattern, value: term.anyOf.map(String), exact: true };
    }
    if (term.contains !== undefined) {
        return { ...pattern, value: term.contains };
    }
    if (term.range) {
        return { ...pattern, range: term.range };
    }
    return { ...pattern, value: "" };
}

async function* emptyIterator(): AsyncIterableIterator<HierarcicalObjectReference> {}

/**
 * The default data access: the scene's own metadata when the view carries it,
 * otherwise the object DB over the network.
 *
 * That either/or already existed inside `utils/search.getObjectData`; this gives
 * it a name and a place, so callers stop passing `db` and `view` around.
 */
export class ObjectDbDataAccess implements ObjectDataAccess {
    readonly kind = "object-db" as const;

    constructor(
        private readonly db: AppObjectDB,
        private readonly view: View,
    ) {}

    isIndexAvailableOffline() {
        return false;
    }

    isDataAvailableOffline() {
        return Boolean(this.view.data);
    }

    /**
     * The scene's own metadata when the view carries it, otherwise the object DB. A
     * `MetaDataEntry` is an `ObjectData` without its `loadMetaData`/`save` methods,
     * which no consumer of this call uses — hence the cast on that branch.
     *
     * Neither read takes a signal, so an aborted call is answered before it starts rather
     * than cancelled in flight — enough to keep a superseded pick from waiting out a
     * request whose answer is already unwanted.
     */
    getObject(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined> {
        if (signal?.aborted) {
            return Promise.resolve(undefined);
        }
        return this.view.data
            ? (this.view.data.getObjectMetaData(id) as unknown as Promise<ObjectData>)
            : this.db.getObjectMetdata(id).catch(() => undefined);
    }

    getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        return this.firstObjectAtPath(path, signal);
    }

    /**
     * By path — the only identity `db.search` accepts — using the path the object
     * already carries, so this is one round trip with no id resolution.
     */
    getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const parentPath = getParentPath(object.path);
        if (!parentPath) {
            return Promise.resolve(undefined);
        }
        return this.firstObjectAtPath(parentPath, signal);
    }

    /**
     * Straight through: `db.search` is already a lazily-paging iterator of exactly
     * this type, keyed by the path the object carries (the root listing is the empty
     * path). No `full: true` — a listing needs identity and node type, not every
     * property of every row; each reference carries `loadMetaData()` for the row that
     * is opened.
     */
    getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        return this.db.search({ parentPath: parent?.path ?? "", descentDepth: 1 }, signal);
    }

    /**
     * One `SearchPattern` per term — the shape `db.search` already takes, since
     * `SearchPattern[]` is ANDed and a `value` array is its own any-of.
     *
     * An empty term list would search for everything rather than nothing, so it
     * answers empty here instead of asking.
     *
     * `full` is the server's own flag of that name: the search preloads every match, so
     * each row arrives complete and its `loadMetaData()` resolves to the row itself
     * instead of asking for `/metadata/{id}`.
     *
     * A lone `id` term is read rather than searched: {@link getObject} goes to the
     * scene's own metadata when the view carries it, and otherwise through the object
     * DB's per-id cache — where `db.search` is a round trip either way, for one row. That
     * read is an object rather than a row, so it satisfies `full` whether or not it was
     * asked for.
     */
    findByProperties(query: PropertyQuery, options?: FindOptions): AsyncIterableIterator<HierarcicalObjectReference> {
        const { terms } = query;
        if (!terms.length) {
            return emptyIterator();
        }
        const id = singleIdPropertyQuery(query);
        if (id !== undefined) {
            return oneObjectAsListing(this.getObject(id, options?.signal));
        }
        return this.db.search({ searchPattern: terms.map(toSearchPattern), full: options?.full }, options?.signal);
    }

    /**
     * No bulk expansion here, so the subtrees are one request per non-leaf match; the
     * fan-out overlaps them with the paging of the matches instead of draining the
     * iterator first. Leaves cost nothing — `db.search` rows carry a real node type.
     */
    async findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array> {
        const { includeDescendants, signal } = options ?? {};

        // A set, not the pieces: subtrees overlap heavily where one match sits under
        // another, so their sum can be many times the distinct answer.
        const ids = new Set<number>();

        await pMap(
            this.findByProperties(query, { signal }),
            async (object) => {
                ids.add(object.id);
                if (!includeDescendants || isLeafNode(object)) {
                    return;
                }
                try {
                    for (const id of await this.getDescendantIds(object, signal)) {
                        ids.add(id);
                    }
                } catch (e) {
                    // One unreadable subtree costs its own ids, not the whole answer, as
                    // in `objectActions.fetchAllDescendantIds`.
                    if (!signal?.aborted) {
                        console.error(`[object-db] could not read the subtree of object ${object.id}:`, e);
                    }
                }
            },
            // No `signal` option: it makes pMap reject with an AbortError, where this
            // interface answers an abort with an empty value. Same reason
            // `objectActions.fetchAllDescendantIds` omits it.
            { concurrency: 6 },
        );

        return signal?.aborted ? new Uint32Array() : new Uint32Array(ids);
    }

    /** The `/resolve` endpoint, which is the object DB's own bulk id mapping. */
    resolveObjectIdsToInstanceIds(objectIds: ObjectId[], signal?: AbortSignal): Promise<(string | null)[] | undefined> {
        return this.resolveInBatches(objectIds, signal, (batch) =>
            this.db.resolveObjectIdsToInstanceIds(batch, signal),
        );
    }

    /** The same endpoint the other way round — see {@link resolveObjectIdsToInstanceIds}. */
    resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal?: AbortSignal,
    ): Promise<(ObjectId | null)[] | undefined> {
        return this.resolveInBatches(instanceIds, signal, (batch) =>
            this.db.resolveInstanceIdsToObjectIds(batch, signal),
        );
    }

    /**
     * One `/resolve` request per {@link RESOLVE_BATCH_SIZE} inputs, folded back in order.
     *
     * A scene whose index carries no mapping answers `404`, which `AppObjectDB` turns into
     * an `undefined` batch and remembers — so when availability is still unknown the first
     * batch goes alone, and a scene without the endpoint costs one request rather than one
     * per batch. Once it is known the batches all go at once.
     *
     * A batch that answers at any width but its own is as unusable as one that did not
     * answer at all: the result is read positionally, so a short batch shifts every later
     * batch's answers onto the wrong ids rather than losing its own.
     */
    private async resolveInBatches<T, R>(
        items: T[],
        signal: AbortSignal | undefined,
        resolveBatch: (batch: T[]) => Promise<R[] | undefined>,
    ): Promise<R[] | undefined> {
        if (this.db.isResolveEndpointAvailable === false || signal?.aborted) {
            return undefined;
        }

        const batches: T[][] = [];
        for (let from = 0; from < items.length; from += RESOLVE_BATCH_SIZE) {
            batches.push(items.slice(from, from + RESOLVE_BATCH_SIZE));
        }

        // The interface answers an abort with its own empty value rather than rejecting,
        // and `undefined` is this method's — the caller falls back to the query, which
        // answers empty for the same aborted signal.
        const resolve = async (batch: T[]) => {
            try {
                return await resolveBatch(batch);
            } catch (e) {
                if (signal?.aborted) {
                    return undefined;
                }
                throw e;
            }
        };

        const mapping: R[] = [];
        if (batches.length > 1 && this.db.isResolveEndpointAvailable === undefined) {
            const probed = batches.shift()!;
            const probe = await resolve(probed);
            if (!probe || probe.length !== probed.length) {
                return undefined;
            }
            mapping.push(...probe);
        }

        // One batch the endpoint could not answer in full makes the whole mapping unusable:
        // it is read positionally, so a hole in it is not something a caller can see.
        const rest = await pMap(batches, resolve, { concurrency: RESOLVE_CONCURRENCY });
        for (const [at, answers] of rest.entries()) {
            if (!answers || answers.length !== batches[at].length) {
                return undefined;
            }
            mapping.push(...answers);
        }
        return mapping;
    }

    /**
     * A negative `descentDepth` searches upwards, but it answers with a flat stream
     * rather than a chain: duplicate-path records (common in IFC/RVT exports) repeat
     * the same ancestors, so the walk keeps the first record per expected path — the
     * chain itself is known upfront from the path string — and caps how many raw
     * records it will scan. Ancestors the search never returns (permission filtering,
     * the cap) are left out.
     *
     * `full: true`, so each record arrives carrying its properties: the chain is walked to
     * be classified, and a listing without them costs a metadata read per ancestor on top
     * of the one search. Chain is not long, so there's little cost in the extra metadata.
     */
    async getAncestors(
        object: HierarcicalObjectReference,
        signal?: AbortSignal,
    ): Promise<HierarcicalObjectReference[]> {
        // Root first, and never the synthesized root itself.
        const expected = [...getParentPaths(object.path)].filter((path) => path !== "");
        if (!expected.length) {
            return [];
        }

        const missing = new Set(expected);
        const byPath = new Map<string, HierarcicalObjectReference>();
        const iterator = this.db.search(
            { descentDepth: -(expected.length + 1), parentPath: object.path, full: true },
            signal,
        );

        // Driven by hand rather than `for await`, which means the generator has to be
        // closed by hand too: every exit below leaves it suspended mid-page otherwise,
        // holding its paging state for as long as the iterator is reachable.
        try {
            for (let scanned = 0; scanned < MAX_ANCESTOR_SCAN && missing.size > 0 && !signal?.aborted; scanned++) {
                const next = await iterator.next();
                if (next.done) {
                    break;
                }
                if (missing.delete(next.value.path)) {
                    byPath.set(next.value.path, next.value);
                }
            }
        } finally {
            await iterator.return?.();
        }

        return expected.map((path) => byPath.get(path)).filter((found) => found !== undefined);
    }

    /**
     * The pre-computed file only — never the subtree walk.
     */
    warmDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<boolean> {
        return preloadDescendantsFastPath({ db: this.db, parentNode: object, abortSignal: signal });
    }

    /**
     * The pre-computed descendants file when the scene has one, otherwise a walk of the
     * subtree. `getDescendants` handles the file, the per-scene cache and the
     * de-duplication of concurrent calls.
     *
     * The walk is a fallback for a backend that answers a missing file by throwing, and
     * only for that: an abort or a refusal is not a scene without descendant files, and
     * answering either with a walk spends a whole subtree on a question that was not
     * asked. (`ProcoreObjectDB.descendants` falls back to the same search itself, so on
     * that backend a throw reaching here has already tried it.)
     *
     * Empty on abort either way, per the interface's contract — never a partial subtree.
     */
    async getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array> {
        try {
            const ids = await getDescendants({ db: this.db, parentNode: object, abortSignal: signal });
            // `getDescendants` is declared `ObjectId[]` but resolves the packed array it
            // caches; a reference's own `descendants` field really is a plain array.
            return ids instanceof Uint32Array ? ids : new Uint32Array(ids);
        } catch (e) {
            // An abort is not a missing file — ours, or a *different* asker's:
            // `getDescendants` joins concurrent reads of the same id without refcounting
            // their signals, so whoever aborts rejects everyone waiting on that read.
            // Falling back would spend the whole subtree walk on an answer nobody is
            // waiting for, and the caller reads a short walk as the real subtree.
            if (signal?.aborted || (e as { name?: string }).name === "AbortError") {
                return new Uint32Array();
            }
            // A refused object stays refused: walking it would answer the denial with
            // whatever part of the subtree the user does have.
            if (e instanceof ObjectAccessForbiddenError) {
                throw e;
            }

            const ids: ObjectId[] = [];
            // `descentDepth: undefined` is the whole subtree.
            for await (const reference of this.db.search({ parentPath: object.path }, signal)) {
                ids.push(reference.id);
            }
            return signal?.aborted ? new Uint32Array() : new Uint32Array(ids);
        }
    }

    /**
     * A row per object, folded here — the object DB has no bulk bounds primitive, so
     * this is the one backend that really does read every object. The rows come from
     * the batched id search rather than a metadata read apiece precisely because they
     * carry `bounds`.
     *
     * An object with no bounds contributes nothing rather than blocking the fold, which
     * is what the interface promises and what the two index backends do natively.
     */
    async getBounds(ids: Iterable<ObjectId>, options?: BoundsOptions): Promise<SelectionBounds | undefined> {
        const { parts = "sphere", signal } = options ?? {};
        const idArr = Array.isArray(ids) ? ids : Array.from(ids);
        if (!idArr.length) {
            return undefined;
        }

        if (idArr.length === 1) {
            const obj = await this.getObject(idArr[0]);
            return objectBoundsAsSelection(obj?.bounds, parts);
        }

        const rows = await this.getObjectsByIds(idArr, signal ?? new AbortController().signal);
        if (signal?.aborted) {
            return undefined;
        }

        const spheres: BoundingSphere[] = [];
        const boxes: { min: ReadonlyVec3; max: ReadonlyVec3 }[] = [];
        for (const row of rows) {
            if (parts !== "box" && row.bounds?.sphere) {
                spheres.push(row.bounds.sphere);
            }
            if (parts !== "sphere" && row.bounds?.box) {
                boxes.push(row.bounds.box);
            }
        }

        const bounds: SelectionBounds = {};
        const sphere = mergeBoundingSpheres(spheres);
        if (sphere) {
            bounds.sphere = sphere;
        }
        const box = unionAABB3(boxes);
        if (box) {
            bounds.box = box;
        }
        return bounds.sphere || bounds.box ? bounds : undefined;
    }

    /** No `climb` here, so the chain is walked and classified by {@link objectAncestors}. */
    async resolveSelection(id: ObjectId, options: SelectionResolutionOptions): Promise<SelectionTarget | undefined> {
        const { mode, classAware, signal } = options;

        // Answered before anything is read, which is the whole point of it.
        if (!classAware && mode === "last-object") {
            return singleSelectionTarget(id);
        }

        const clicked = await this.getObject(id, signal);
        if (!clicked || signal?.aborted) {
            return undefined;
        }

        return classAware
            ? this.resolveByNodeClass(clicked, mode, signal)
            : resolveSelectionLegacy({ access: this, clicked, mode, signal });
    }

    /** Nothing of its own to release: both the db and the view outlive this. */
    dispose() {}

    private async resolveByNodeClass(
        clicked: ObjectData,
        mode: SelectionResolutionMode,
        signal?: AbortSignal,
    ): Promise<SelectionTarget | undefined> {
        const clickedIsObject = getNodeClass(clicked.properties) === NodeClass.Object;

        // A click lands on the deepest node of its own chain, so a picked object already
        // *is* the deepest object.
        if (clickedIsObject && mode === "last-object") {
            return selectionTargetFor(this, clicked, "resolved", signal);
        }

        // Only nesting can move `first-object` off a picked object, so its subtree is read
        // speculatively alongside the chain: the common non-nested click then costs what
        // `last-object` does instead of a second round trip.
        const [objects, clickedHighlight] = await Promise.all([
            this.objectAncestors(clicked, signal),
            clickedIsObject ? highlightIdsFor(this, clicked, signal) : undefined,
        ]);

        // Root first, so the shallowest object leads and the deepest ends it.
        const target = mode === "first-object" ? objects[0] : objects.at(-1);
        if (target) {
            return selectionTargetFor(this, target, "resolved", signal);
        }

        if (clickedHighlight) {
            return {
                mainObjectId: clicked.id,
                mainObject: clicked,
                highlightIds: clickedHighlight,
                outcome: "resolved",
            };
        }

        // Nothing above a picked node that is not one itself: a file of only containers and
        // fragments, or a scene parsed before node classes. Legacy rather than the picked
        // node alone, so class-aware never resolves *less* than pre-`NodeClass` would have.
        return resolveSelectionLegacy({ access: this, clicked, mode, signal });
    }

    /**
     * The ancestors that are objects, root first.
     *
     * {@link getAncestors} asks for `full` records, so each row already carries what says
     * its class and `loadMetaData` resolves to the row itself — the whole chain costs the
     * one upward search. A row that somehow arrives without its metadata is dropped rather
     * than failing the click, as reading it through `getObject` used to be.
     *
     * `ObjectData` rather than the bare references, so the resolved ancestor reaches
     * {@link selectionTargetFor} as a metadata read and fills `mainObject` for the panels
     * that open on it.
     */
    private async objectAncestors(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData[]> {
        const ancestors = await this.getAncestors(object, signal);
        const classified = await Promise.all(
            ancestors.map((ancestor) => ancestor.loadMetaData().catch(() => undefined)),
        );
        return classified.filter(
            (ancestor): ancestor is ObjectData => getNodeClass(ancestor?.properties) === NodeClass.Object,
        );
    }

    private async getObjectsByIds(idArr: ObjectId[], abortSignal: AbortSignal) {
        const { data } = this.view;
        if (data) {
            return getObjectsByIdsOffline(this.view, idArr, abortSignal);
        }

        return batchedPropertySearch({
            db: this.db,
            property: "id",
            value: idArr.map(String),
            abortSignal,
        });
    }

    /**
     * The node at `path` itself — `descentDepth: 0` — with its metadata.
     *
     * Only the first record is wanted, so the iterator is closed rather than left
     * suspended: `next()` alone never runs the generator's `return`, unlike the `break`
     * of a `for await`.
     */
    private async firstObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const iterator = this.db.search({ parentPath: path, descentDepth: 0, full: true }, signal);
        try {
            const first = await iterator.next();
            return first.value ? await first.value.loadMetaData() : undefined;
        } catch {
            return undefined;
        } finally {
            await iterator.return?.();
        }
    }
}
